/**!*******************************************************************
 *
 * Copyright (c) Epic AI GmbH. All rights reserved.
 * Author:  Alexander Claas
 * 
 ********************************************************************!*/

import { ActionContext, ActionTree } from "vuex";
import { IAdminState } from "./state";
import { IRootState } from "@/store/modules/root/state";
import { IMutations } from "./mutations";
import { AdminStore } from "@/store/constants";
import { getAllUsers, getUser } from "@/api/admin/users";
import { getAllGroups, getGroup } from "@/api/admin/groups";
import { getAllBots } from "@/api/bot";
import { getAllInvoices, getAllMethods, getSubscription } from "@/api/admin/billing";
import { ISubscriptionResult } from "@chatcaptain/types/billing";

export enum Actions {
  LOAD_USERS = "LOAD_USERS",
  UPDATE_USER = "UPDATE_USER",
  LOAD_GROUPS = "LOAD_GROUPS",
  UPDATE_GROUP = "UPDATE_GROUP",
  LOAD_CHATBOTS = "LOAD_CHATBOTS",
  LOAD_SUBSCRIPTION = "LOAD_SUBSCRIPTION",
  LOAD_PAYMENT_METHODS = "LOAD_PAYMENT_METHODS",
  LOAD_INVOICES = "LOAD_INVOICES"
}

export type AugmentedActionContext = {
  commit<K extends keyof IMutations>(
    key: K,
    payload: Parameters<IMutations[K]>[1]
  ): ReturnType<IMutations[K]>;
} & Omit<ActionContext<IAdminState, IRootState>, "commit">;

export interface IActions {
  [Actions.LOAD_USERS]({ commit }: AugmentedActionContext): void;
  [Actions.UPDATE_USER]({ commit }: AugmentedActionContext, userId: string): void;
  [Actions.LOAD_GROUPS]({ commit }: AugmentedActionContext): void;
  [Actions.LOAD_CHATBOTS]({ commit }: AugmentedActionContext): void;
  [Actions.LOAD_SUBSCRIPTION]({ commit }: AugmentedActionContext): void;
  [Actions.LOAD_PAYMENT_METHODS]({ commit }: AugmentedActionContext): void;
  [Actions.LOAD_INVOICES]({ commit }: AugmentedActionContext): void;
}

export const actions: ActionTree<IAdminState, IRootState> & IActions = {
  [Actions.LOAD_USERS]({ commit }) {
    getAllUsers().then(users => {
      if(users) commit(AdminStore.Mutations.SET_USERS, users);
    });
  },
  [Actions.UPDATE_USER]({ commit }, userId: string) {
    getUser(userId).then(user => {
      if(user) commit(AdminStore.Mutations.UPDATE_USER, user);
    })
  },
  [Actions.LOAD_GROUPS]({ commit }) {
    getAllGroups().then(groups => {
      if(groups) commit(AdminStore.Mutations.SET_GROUPS, groups);
    });
  },
  [Actions.UPDATE_GROUP]({ commit }, groupId: string) {
    getGroup(groupId).then(group => {
      if(group) commit(AdminStore.Mutations.UPDATE_GROUP, group);
    });
  },
  [Actions.LOAD_CHATBOTS]({ commit }) {
    getAllBots().then(bots => {
      if(bots) commit(AdminStore.Mutations.SET_CHATBOTS, bots);
    });
  },
  [Actions.LOAD_SUBSCRIPTION]({ commit }) {
    getSubscription().then((subscription: ISubscriptionResult | null) => {
      commit(AdminStore.Mutations.SET_SUBSCRIPTION, subscription);
    })
  },
  [Actions.LOAD_PAYMENT_METHODS]({ commit }) {
    getAllMethods().then(methods => {
      if(methods) commit(AdminStore.Mutations.SET_PAYMENT_METHODS, methods);
    });
  },
  [Actions.LOAD_INVOICES]({ commit }) {
    getAllInvoices().then(invoices => {
      if(invoices) commit(AdminStore.Mutations.SET_INVOICES, invoices);
    });
  }
}